<template>
  <div class="preview-section mb-4">
    <div v-if="selectedElement" :class="['card', { 'readonly-mode': props.readOnly }]">
      <cardHeader
        :numOrder="getQuestionNumber() === 'N/A' ? numOrder : getQuestionNumber()"
        :elementType="selectedElement.el_Type"
        :mode="'Render'"
        :isAgent="isAgent" />

      <div class="card-body">
        <!-- Main Prompt -->
        <div class="mb-2">
          <h3>{{ selectedElement.el_Text }}</h3>
        </div>

        <!-- Rich Text Content -->
        <div class="quill-editor" ref="quillEditor" v-show="hasQuillContent"></div>

        <!-- Feedback Display -->
        <div class="AllFeedbacks" v-if="showReport">
          <div v-if="isCorrect && selectedElement.feedback?.correct" class="PositiveFeebackContainer">
            {{ selectedElement.feedback.correct }}
          </div>
          <div v-else-if="!isCorrect && selectedElement.feedback?.incorrect" class="NegativeFeebackContainer">
            {{ selectedElement.feedback.incorrect }}
          </div>

          <!-- Zone-specific feedback -->
          <div v-if="zoneFeedbacks.length > 0" class="zone-feedbacks mt-2">
            <strong>Zone Feedback:</strong>
            <ul>
              <li v-for="(fb, idx) in zoneFeedbacks" :key="idx">{{ fb }}</li>
            </ul>
          </div>
        </div>

        <!-- Canvas for zone clicking -->
        <div class="canvas-wrapper mt-3 mb-3">
          <div ref="canvasRef" class="konva-container"></div>
        </div>

        <!-- Hint Button -->
        <div class="hintContainer" v-if="displayHintButton">
          <button
            @click="HandlerShowHint"
            class="hintBT btn btn-primary btn-sm"
            :disabled="props.readOnly">
            {{ $t('Hint') }}!
          </button>
          <div v-if="showHint" class="hintMessage">{{ selectedElement.tip }}</div>
        </div>
      </div>

      <!-- Report Container -->
      <div class="ReportContainer" v-if="showReport">
        <div class="row">
          <div class="col">
            <h5>Results:</h5>
          </div>
          <div class="col" style="text-align: right;"><strong>Date:</strong> {{ currentDate }}</div>
          <hr />
          <p><strong>Clicked Zones:</strong> {{ results.clickedZones.join(', ') || 'None' }}</p>
          <p><strong>Correct Zones:</strong> {{ results.correctZones.join(', ') }}</p>
          <p><strong>Score:</strong> {{ results.score }}</p>
          <p><strong>Max Score:</strong> {{ selectedElement.maxScore }}</p>
          <p><strong>Penalties:</strong> {{ results.penalties }}</p>
          <p><strong>Hint was used:</strong> {{ results.hint }}</p>
        </div>
      </div>

      <!-- Footer -->
      <cardFooterLearningMode
        v-if="publicationStore.publicationData?.mode !== 'exam'"
        :showReport="showReport"
        :submitAnswers="showReportOnSubmit"
        :closeReport="closeReport"
        :resetSelections="resetAnswers" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import Konva from 'konva'
import Quill from 'quill'
import cardHeader from './cardHeader.vue'
import cardFooterLearningMode from './cardFooterLearningMode.vue'
import { useResponsesStore } from '@/stores/useResponsesStore'
import { usePublicationStore } from '@/stores/usePublicationStore'
import { EUROPE_DEFAULT_IMAGE } from './composables/defaultImages.js'

const props = defineProps({
  question: { type: Object, required: true },
  testData: { type: Object, required: true },
  numOrder: [Number, null],
  questionIndexMap: { type: Object, required: false },
  restoredAnswer: { type: Object, default: () => ({}) },
  readOnly: { type: Boolean, default: false },
  isAgent: { type: Boolean, default: false }
})

const emit = defineEmits(['interaction-started'])

// Stores
const responsesStore = useResponsesStore()
const publicationStore = usePublicationStore()

// Refs
const canvasRef = ref(null)
const quillEditor = ref(null)
let quillInstance = null
let stage = null
let bgLayer = null
let zoneLayer = null

// State
const selectedElement = computed(() => props.question)
const showReport = ref(false)
const showHint = ref(false)
const clickedZones = ref([])
const hintUsed = ref(false)
const currentDate = ref(new Date().toLocaleString())

// User identification
const token = localStorage.getItem('token')
const decodedToken = decodeToken(token)
const userId = decodedToken?._id || decodedToken?.sub

// Computed
const hasQuillContent = computed(() => {
  return selectedElement.value?.el_RichText && selectedElement.value.el_RichText.trim() !== ''
})

const displayHintButton = computed(() => {
  return selectedElement.value?.tip && selectedElement.value.tip.trim() !== ''
})

const getQuestionNumber = () => {
  if (!selectedElement.value || !props.questionIndexMap) return 'N/A'
  return props.questionIndexMap[selectedElement.value.el_ID] || 'N/A'
}

// Zone-specific feedbacks
const zoneFeedbacks = computed(() => {
  if (!showReport.value) return []
  const zones = selectedElement.value?.el_Data || []
  const feedbacks = []

  clickedZones.value.forEach(zoneId => {
    const zone = zones.find(z => z.id === zoneId)
    if (zone?.feedback) {
      feedbacks.push(zone.feedback)
    }
  })

  return feedbacks
})

// Results calculation
const results = computed(() => {
  const zones = selectedElement.value?.el_Data || []
  const correctZones = zones.filter(z => z.isCorrect)
  const clickedCorrect = clickedZones.value.filter(cz => correctZones.some(z => z.id === cz))
  const clickedIncorrect = clickedZones.value.filter(cz => !correctZones.some(z => z.id === cz))

  const score = clickedCorrect.reduce((sum, zoneId) => {
    const zone = zones.find(z => z.id === zoneId)
    return sum + (zone?.score || 0)
  }, 0)

  const penalties = clickedIncorrect.reduce((sum, zoneId) => {
    const zone = zones.find(z => z.id === zoneId)
    return sum + (zone?.penalty || 0)
  }, 0)

  const clickedZoneNames = clickedZones.value.map(zoneId => {
    const zone = zones.find(z => z.id === zoneId)
    return zone?.name || zoneId
  })

  const correctZoneNames = correctZones.map(z => z.name || z.id)

  return {
    clickedZones: clickedZoneNames,
    correctZones: correctZoneNames,
    score: Math.max(0, score - penalties),
    penalties,
    hint: hintUsed.value
  }
})

const isCorrect = computed(() => {
  const zones = selectedElement.value?.el_Data || []
  const correctZones = zones.filter(z => z.isCorrect)

  if (selectedElement.value?.multiple) {
    // Multiple mode: all correct zones must be clicked, no incorrect ones
    return correctZones.every(z => clickedZones.value.includes(z.id)) &&
           clickedZones.value.every(cz => correctZones.some(z => z.id === cz))
  } else {
    // Single mode: exactly one correct zone must be clicked
    return clickedZones.value.length === 1 &&
           correctZones.some(z => z.id === clickedZones.value[0])
  }
})

// Methods
const HandlerShowHint = () => {
  showHint.value = !showHint.value
  hintUsed.value = true
}

const closeReport = () => {
  showReport.value = false
}

const showReportOnSubmit = () => {
  showReport.value = true
  currentDate.value = new Date().toLocaleString()

  // Save response with proper structure
  const response = {
    userId: userId,
    questionId: selectedElement.value.el_ID,
    type: selectedElement.value.el_Type,
    questionLabel: selectedElement.value.el_Text,
    el_ID: selectedElement.value.el_ID,
    el_Type: selectedElement.value.el_Type,
    answered: true,
    response: {
      clickedZones: clickedZones.value
    },
    score: results.value.score,
    maxScore: selectedElement.value.maxScore,
    correct: results.value.isCorrect,
    timestamp: new Date().toISOString(),
    hintUsed: hintUsed.value
  }

  responsesStore.saveResponse(response)
  emit('interaction-started', selectedElement.value.el_ID)
}

const resetAnswers = () => {
  clickedZones.value = []
  showReport.value = false
  showHint.value = false
  hintUsed.value = false

  // Clear response
  responsesStore.saveResponse(selectedElement.value.el_ID, null)

  // Redraw canvas to clear highlights
  if (zoneLayer) {
    drawZones()
  }
}

// Canvas initialization
const initCanvas = () => {
  if (!canvasRef.value || !selectedElement.value) return

  const imageObj = new Image()
  imageObj.onload = () => {
    const stageWidth = selectedElement.value.el_CanvasSize?.width || imageObj.width
    const stageHeight = selectedElement.value.el_CanvasSize?.height || imageObj.height

    stage = new Konva.Stage({
      container: canvasRef.value,
      width: stageWidth,
      height: stageHeight
    })

    // Background layer
    bgLayer = new Konva.Layer()
    const bgImage = new Konva.Image({
      image: imageObj,
      width: stageWidth,
      height: stageHeight
    })
    bgLayer.add(bgImage)
    stage.add(bgLayer)

    // Zone layer
    zoneLayer = new Konva.Layer()
    stage.add(zoneLayer)

    drawZones()

    // Restore saved answer from prop
    if (props.restoredAnswer?.clickedZones) {
      clickedZones.value = props.restoredAnswer.clickedZones
      drawZones()
    }
  }

  // Get image source
  const imageSrc = getBackgroundImageSource()
  imageObj.src = imageSrc

  imageObj.onerror = () => {
    console.error('Failed to load image, using default')
    imageObj.src = EUROPE_DEFAULT_IMAGE
  }
}

const getBackgroundImageSource = () => {
  if (selectedElement.value?.el_data?.backgroundImage?.base64) {
    return selectedElement.value.el_data.backgroundImage.base64
  }
  return EUROPE_DEFAULT_IMAGE
}

const drawZones = () => {
  if (!zoneLayer) return

  zoneLayer.destroyChildren()
  const zones = selectedElement.value?.el_Data || []

  zones.forEach(zone => {
    let shape

    const isClicked = clickedZones.value.includes(zone.id)
    const fillColor = isClicked ? 'rgba(0, 123, 255, 0.5)' : 'rgba(0, 123, 255, 0.1)'
    const strokeColor = isClicked ? 'blue' : 'rgba(0, 123, 255, 0.3)'

    const commonProps = {
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: 2,
      id: zone.id,
      name: 'clickableZone'
    }

    switch (zone.type) {
      case 'circle':
        shape = new Konva.Circle({
          ...commonProps,
          x: zone.x,
          y: zone.y,
          radius: zone.radius
        })
        break
      case 'rect':
        shape = new Konva.Rect({
          ...commonProps,
          x: zone.x,
          y: zone.y,
          width: zone.width,
          height: zone.height
        })
        break
      case 'freeform':
        shape = new Konva.Line({
          ...commonProps,
          points: zone.points,
          x: zone.x || 0,
          y: zone.y || 0,
          closed: true
        })
        break
    }

    if (shape) {
      shape.on('click', () => handleZoneClick(zone.id))
      shape.on('mouseenter', () => {
        document.body.style.cursor = 'pointer'
        if (!clickedZones.value.includes(zone.id)) {
          shape.fill('rgba(0, 123, 255, 0.3)')
          zoneLayer.draw()
        }
      })
      shape.on('mouseleave', () => {
        document.body.style.cursor = 'default'
        if (!clickedZones.value.includes(zone.id)) {
          shape.fill('rgba(0, 123, 255, 0.1)')
          zoneLayer.draw()
        }
      })

      zoneLayer.add(shape)
    }
  })

  zoneLayer.draw()
}

const handleZoneClick = (zoneId) => {
  if (props.readOnly || showReport.value) return

  if (selectedElement.value?.multiple) {
    // Multiple mode: toggle zone
    if (clickedZones.value.includes(zoneId)) {
      clickedZones.value = clickedZones.value.filter(id => id !== zoneId)
    } else {
      clickedZones.value.push(zoneId)
    }
  } else {
    // Single mode: replace selection
    clickedZones.value = [zoneId]
  }

  // Save to store immediately with proper structure
  const response = {
    userId: userId,
    questionId: selectedElement.value.el_ID,
    type: selectedElement.value.el_Type,
    questionLabel: selectedElement.value.el_Text,
    el_ID: selectedElement.value.el_ID,
    el_Type: selectedElement.value.el_Type,
    answered: clickedZones.value.length > 0,
    response: {
      clickedZones: clickedZones.value
    },
    score: results.value.score,
    maxScore: selectedElement.value.maxScore,
    correct: results.value.isCorrect,
    timestamp: new Date().toISOString(),
    hintUsed: hintUsed.value
  }

  responsesStore.saveResponse(response)

  drawZones()
  emit('interaction-started', selectedElement.value.el_ID)
}

// Quill initialization
onMounted(() => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      readOnly: true,
      modules: { toolbar: false }
    })

    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText
    }
  }

  nextTick(() => {
    initCanvas()
  })
})

watch(() => props.question, (newElement) => {
  if (newElement && quillInstance) {
    quillInstance.root.innerHTML = newElement.el_RichText || ''
  }

  nextTick(() => {
    if (stage) {
      stage.destroy()
      stage = null
    }
    initCanvas()
  })
}, { deep: true })

watch(clickedZones, () => {
  drawZones()
}, { deep: true })

// Helper function to decode JWT token
function decodeToken(token) {
  if (!token) {
    console.error('Le token est null ou non défini')
    return null
  }
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload))
  } catch (error) {
    console.error('Erreur lors du décodage du token :', error)
    return null
  }
}
</script>

<style scoped>
.preview-section {
  padding: 1rem;
}

.canvas-wrapper {
  border: 2px solid #ddd;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  display: inline-block;
}

.konva-container {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.readonly-mode {
  pointer-events: none;
  opacity: 0.7;
}

.PositiveFeebackContainer {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-top: 1rem;
}

.NegativeFeebackContainer {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-top: 1rem;
}

.zone-feedbacks {
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
  padding: 0.75rem;
  border-radius: 0.25rem;
}

.hintContainer {
  margin-top: 1rem;
}

.hintMessage {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
  padding: 0.75rem;
  border-radius: 0.25rem;
  margin-top: 0.5rem;
}

.ReportContainer {
  background-color: #e9ecef;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-top: 1rem;
}
</style>
